import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ml_asset, use_slice_workspace } from '@/hooks/store/use_slices';
import { use_workspace_balance } from '@/hooks/use_workspace_balance';
import i18n_page_diffusion_models from '@/locales/keys/i18n_page_diffusion_models.json';
import { ViewComponentLinePlaceholder } from '@/pages/_base/views/ViewComponentLinePlaceholder';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_ml_asset_rent } from '@/state/diffusion_models_ud/remote_ml_asset_rent';
import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { RequestDiffusionModelsUDRent } from '@shared/dto/+model/reservation/_requests/request_diffusion_models_ud_rent';
import { DTODiffusionModelUDBase } from '@shared/dto/+model/sd/_base/dto_diffusion_model_ud_base';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithHoverAnimateBorder } from '../../../_base/buttons/button_with_hover_animate_border';

interface Props {
  className?: string;
  is_parent_hovered: boolean;
  diffusion_model: DTODiffusionModelUDBase;
  on_click?: (diffusion_model_ud: DTODiffusionModelUDBase) => void;
}

export function DiffusionModelsButtonRentAnimatedUD({ className = '', is_parent_hovered, diffusion_model, on_click }: Props) {
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_DIFFUSION_MODELS);

  const dispatch = use_dispatch();

  const workspace_balance = use_workspace_balance();

  const { workspace_current, current_workspace_user } = use_slice_workspace(['workspace_current', 'current_workspace_user']);
  const { reservations_current_workspace_active_map } = use_slice_ml_asset(['reservations_current_workspace_active_map']);

  const [is_loading, set_is_loading] = useState(false);

  const title = useMemo(() => get_title(), [diffusion_model.rent_price, reservations_current_workspace_active_map, diffusion_model._id, workspace_current._id]);
  const will_spend_bonus_credits = useMemo(() => get_will_spend_bonus_credits(), [workspace_current, diffusion_model, workspace_balance]);

  /*
   * EFFECTS
   */

  /*
   * EVENTS
   */

  async function on_click_internal() {
    on_click?.(diffusion_model);

    if (workspace_current.is_stub()) return;

    set_is_loading(true);

    let request = new RequestDiffusionModelsUDRent();
    request.workspace_id = workspace_current._id;
    request.diffusion_model_id = diffusion_model._id;

    await dispatch(remote_ml_asset_rent(request));

    set_is_loading(false);
  }

  /*
   * METHODS
   */

  function get_title() {
    let out = `${t(i18n_page_diffusion_models.button.rent.rent)}: ${diffusion_model.rent_price}`;

    const current_model_reservations_map = reservations_current_workspace_active_map.get_value_or_default(diffusion_model._id, new Map());

    if (Array.from(current_model_reservations_map.values()).length > 0) {
      out = `${t(i18n_page_diffusion_models.button.rent.prolongate)}: ${diffusion_model.rent_price}`;
    }

    return out;
  }

  function get_will_spend_bonus_credits() {
    if (workspace_current.is_stub()) return false;
    if (!workspace_current.is_personal) return false;

    let balance_after_purchase = workspace_balance - CONSTANTS__shared.CREDITS_GUARANTEED_PER_DAY_PERSONAL - diffusion_model.rent_price;

    return balance_after_purchase < 0;
  }

  function is_disabled() {
    if (is_loading) return true;
    if (workspace_current.is_stub()) return true;
    if (workspace_balance < diffusion_model.rent_price) return true;

    let out = false;

    if (workspace_current.is_personal) {
      out = will_spend_bonus_credits;
    }

    return out;
  }

  /*
   * RENDERS
   */

  function try_render_tooltip(button: ReactNode) {
    let out = button;

    let title = workspace_balance < diffusion_model.rent_price ? t(i18n_page_diffusion_models.tooltip.low_credits_balance) : '';

    title = will_spend_bonus_credits ? `${title}\n${t(ENUM__error_type.USING_GUARANTEED_CREDITS_NOT_ALLOWED)}` : title;

    out = (
      <UI_PRIMITIVE__tooltip tooltip_title={title} side="right" delay_duration={0}>
        <div className="w-full">{button}</div>
      </UI_PRIMITIVE__tooltip>
    );

    return out;
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.DIFFUSION_MODELS_RESERVATION_VIEW)) return null;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.DIFFUSION_MODELS_RESERVATION_UPDATE))
    return <ViewComponentLinePlaceholder />;

  return try_render_tooltip(
    <ButtonWithHoverAnimateBorder {...{ is_parent_hovered, title, className, on_click: on_click_internal }} disabled={is_disabled()} />,
  );
}
